import appStorage from 'storage/appStorage'
import { ComponentTypes } from 'types/selectComponent'

const TOGGLE_GLOBAL_CART = 'TOGGLE_GLOBAL_CART'
const SET_IGNORE_CLICK_OUTSIDE = 'SET_IGNORE_CLICK_OUTSIDE'
const SET_EXPANDED_COMPONENT_LIST = 'SET_EXPANDED_COMPONENT_LIST'
const ADD_COMPONENT_TO_EXPANDED_LIST = 'ADD_COMPONENT_TO_EXPANDED_LIST'
const REMOVE_COMPONENT_FROM_EXPANDED_LIST = 'REMOVE_COMPONENT_FROM_EXPANDED_LIST'

export const CART_VISIBILITY_KEY = 'cartVisibility'
export const EXPANDED_COMPONENT_LIST_KEY = 'expandedComponentList'

export type GlobalCartComponentTypes = ComponentTypes | 'mounting'

type ToggleGlobalCart = {
  type: typeof TOGGLE_GLOBAL_CART
  value: boolean
}

type SetIgnoreClickOutside = {
  type: typeof SET_IGNORE_CLICK_OUTSIDE
  value: boolean
}

type SetExpandedComponentList = {
  type: typeof SET_EXPANDED_COMPONENT_LIST
  value: GlobalCartComponentTypes[]
}

type AddComponentToExpandedList = {
  type: typeof ADD_COMPONENT_TO_EXPANDED_LIST
  value: GlobalCartComponentTypes
}

type RemoveComponentFromExpandedList = {
  type: typeof REMOVE_COMPONENT_FROM_EXPANDED_LIST
  value: GlobalCartComponentTypes
}

type GlobalCartActionTypes =
  | ToggleGlobalCart
  | SetIgnoreClickOutside
  | SetExpandedComponentList
  | AddComponentToExpandedList
  | RemoveComponentFromExpandedList

export type GlobalCartState = {
  isOpen: boolean
  ignoreClickOutside: boolean
  expandedComponentList: GlobalCartComponentTypes[] | null
}

// Helper functions to read initial state from sessionStorage/localStorage
// We use sessionStorage for cart visibility and localStorage for expanded component list
// This allows the cart visibility to reset on a different session while keeping the expanded component list persistent
const getInitialCartVisibility = (): boolean => {
  try {
    const value = sessionStorage.getItem(CART_VISIBILITY_KEY)
    return value ? JSON.parse(value) : false
  } catch (error) {
    console.error('Error reading cart visibility from sessionStorage:', error)
    return false
  }
}

const getInitialExpandedComponentList = (): GlobalCartComponentTypes[] => {
  try {
    const value = appStorage.getString(EXPANDED_COMPONENT_LIST_KEY)
    return value ? JSON.parse(value) : []
  } catch (error) {
    console.error('Error reading expanded component list from sessionStorage:', error)
    return []
  }
}

const initialState: GlobalCartState = {
  isOpen: getInitialCartVisibility(),
  expandedComponentList: null,
  ignoreClickOutside: false,
}

export default function globalCartReducer(
  state: GlobalCartState = initialState,
  action: GlobalCartActionTypes
): GlobalCartState {
  switch (action.type) {
    case TOGGLE_GLOBAL_CART:
      return {
        ...state,
        isOpen: action.value,
      }

    case SET_IGNORE_CLICK_OUTSIDE:
      return {
        ...state,
        ignoreClickOutside: action.value,
      }
    case SET_EXPANDED_COMPONENT_LIST:
      return {
        ...state,
        expandedComponentList: action.value,
      }

    case ADD_COMPONENT_TO_EXPANDED_LIST: {
      // If the list is null, initialize it with the new component
      if (!state.expandedComponentList) {
        return {
          ...state,
          expandedComponentList: [action.value],
        }
      }
      // If the component is not already in the list, add it
      if (!state.expandedComponentList.includes(action.value)) {
        return {
          ...state,
          expandedComponentList: [...state.expandedComponentList, action.value],
        }
      }
      return state // No change if already in the list
    }

    case REMOVE_COMPONENT_FROM_EXPANDED_LIST: {
      // If the list is null, return the state unchanged
      if (!state.expandedComponentList) {
        return state
      }
      // If the component is not in the list, return the state unchanged
      if (!state.expandedComponentList.includes(action.value)) {
        return state
      }
      // Filter out the component from the list
      return {
        ...state,
        expandedComponentList: state.expandedComponentList.filter((component) => component !== action.value),
      }
    }

    default:
      return state
  }
}

export const toggleGlobalCart = (isOpen: boolean): ToggleGlobalCart => ({
  type: TOGGLE_GLOBAL_CART,
  value: isOpen,
})

export const setIgnoreClickOutside = (ignore: boolean): SetIgnoreClickOutside => ({
  type: SET_IGNORE_CLICK_OUTSIDE,
  value: ignore,
})

export const initializeExpandedComponentList = (): SetExpandedComponentList => ({
  type: SET_EXPANDED_COMPONENT_LIST,
  value: getInitialExpandedComponentList(),
})

export const setExpandedComponentList = (
  expandedComponentList: GlobalCartComponentTypes[]
): SetExpandedComponentList => ({
  type: SET_EXPANDED_COMPONENT_LIST,
  value: expandedComponentList,
})

export const addComponentToExpandedList = (componentType: GlobalCartComponentTypes): AddComponentToExpandedList => ({
  type: ADD_COMPONENT_TO_EXPANDED_LIST,
  value: componentType,
})

export const removeComponentFromExpandedList = (
  componentType: GlobalCartComponentTypes
): RemoveComponentFromExpandedList => ({
  type: REMOVE_COMPONENT_FROM_EXPANDED_LIST,
  value: componentType,
})
